import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Col, Row } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import TextArea from "../../common/textfield/TextArea";
import { Button } from "../../common/Button";
import * as Yup from "yup";
import { editEncounterIntakeQuestions } from "../api/Encounter";
import { failed, success } from "../../common/Toastify";
import { getQuestionnaireResponseById, editQuestionnaireResponse } from "../../api/Questionnaire";
import { useSelector } from "react-redux";
import { buildQuestionResponseFromQR } from "../../../utils/questionnaireResponseUtils";

const EditReasonForCallModal = ({ data, onShow, onHide, onSuccess, dialogClassName = "custom-dialog", onSubmitOverride }) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const [questionnaireResponse, setQuestionnaireResponse] = useState(null);
  const [qrError, setQrError] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  const user = useSelector((state) => state?.auth?.user);

  const qrReference = data?.intakeQuestions?.questionnaireResponseId?.reference;
  const questionnaireResponseId = qrReference?.split("/")?.[1] || null;

  const currentReason =
    data?.intakeQuestions?.intakeReason || "";

  const formik = useFormik({
    initialValues: {
      reason: currentReason,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      reason: Yup.string().max(500, "Maximum length is 500 characters."),
    }),
    onSubmit: async (values) => {
      values.reason = values.reason?.trim();
      setBtnLoading(true);

      const trimmedReason = values.reason?.trim() || "";

      try {
        if (onSubmitOverride) {
          await onSubmitOverride({ reason: trimmedReason });
          onSuccess?.();
          onHide();
          return;
        }
        // 1) Update encounter intakeQuestions.intakeReason
        await editEncounterIntakeQuestions({
          encounterId: data?.id,
          payload: {
            intakeQuestions: { intakeReason: trimmedReason },
          },
        });

        // 2) If we have a QuestionnaireResponse, update REASON there as well
        if (questionnaireResponseId && questionnaireResponse) {
          const qr = questionnaireResponse;
          const questionResponse = buildQuestionResponseFromQR(qr, trimmedReason);

          if (questionResponse) {
            await editQuestionnaireResponse(qr.id, {
              encounterId: qr.encounterId,
              questionnaire: qr.questionnaire,
              patientID: qr.source,
              questionResponse,
            });
          }
        }

        success("Reason for call updated successfully.");
        onSuccess?.();
        onHide();
      } catch (err) {
        failed(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err.message ||
            "Reason for call update was unsuccessful."
        );
      } finally {
        setBtnLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!questionnaireResponseId) return;

    setQrLoading(true);
    setQrError(null);

    getQuestionnaireResponseById(questionnaireResponseId)
      .then((res) => {
        const qr = res?.data?.data || res?.data || res;
        if (!qr || !Array.isArray(qr.item)) {
          setQrError("Unable to load Questionnaire Response.");
          return;
        }
        setQuestionnaireResponse(qr);
      })
      .catch((err) => {
        setQrError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err.message ||
            "Failed to load Questionnaire Response."
        );
      })
      .finally(() => setQrLoading(false));
  }, [questionnaireResponseId]);

  return (
    <Modal
      backdropClassName
      backdrop="static"
      show={onShow}
      onHide={onHide}
      size="lg"
      aria-labelledby="edit-reason-modal-title"
      centered
      className={dialogClassName}
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title id="edit-reason-modal-title">
          Edit Reason For Call
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form
          className="common-form border-fields"
          onSubmit={formik.handleSubmit}
        >
          <Row>
            <Col>
              <TextArea
                keyField="reason"
                label="Reason for Call"
                formik={formik}
                placeholder="Enter reason for call..."
                required={true}
              />
            </Col>
          </Row>
          {qrLoading && (
            <div className="mt-2 text-muted" style={{ fontSize: 12 }}>
              Loading questionnaire details...
            </div>
          )}
          {qrError && (
            <div className="mt-2 error-text" style={{ fontSize: 12 }}>
              {qrError} Only the encounter reason will be updated.
            </div>
          )}
          <div className="btn-wrap">
            <Button onClick={onHide} variant="secondary" title="Cancel">
              Cancel
            </Button>
            <Button type="submit" isLoading={btnLoading}>
              Update
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default EditReasonForCallModal;
